/** Screen lock after 5 minutes without a key press or touch (SOW Module 1). */
export const IDLE_LOCK_MS = 5 * 60_000;

export class IdleTimer {
  private last: number;
  constructor(private readonly timeoutMs: number = IDLE_LOCK_MS, now: number = Date.now()) {
    this.last = now;
  }
  touch(now: number = Date.now()): void {
    this.last = now;
  }
  isIdle(now: number = Date.now()): boolean {
    return now - this.last >= this.timeoutMs;
  }
}

/**
 * Offline unlock: a salted PBKDF2 verifier of the cashier's password, made at sign-in. Unlocking works without the
 * server, and the password itself is never stored.
 */
export interface Verifier { username: string; salt: string; hash: string; iterations: number; createdAt: number }

/** Like the server's lockout (SOW Modules 61 and 72): five wrong passwords and the terminal needs the network. */
export const OFFLINE_UNLOCK_MAX_FAILS = 5;
/** A disabled or signed-out cashier can keep unlocking offline for at most this long after the last online sign-in. */
export const VERIFIER_MAX_AGE_MS = 24 * 3_600_000;

/** Why an offline unlock is not allowed any more, or null when the password may be checked. */
export function offlineUnlockRefusal(v: Verifier, failures: number, now: number = Date.now()): string | null {
  if (failures >= OFFLINE_UNLOCK_MAX_FAILS) return "Too many wrong passwords. This terminal must reach the server to unlock; call the supervisor.";
  if (!Number.isFinite(v.createdAt) || now - v.createdAt > VERIFIER_MAX_AGE_MS) return "Offline unlock has expired (24 hours after the last online sign-in). Wait for the network.";
  return null;
}

const hex = (buf: ArrayBuffer | Uint8Array) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
const unhex = (s: string) => new Uint8Array((s.match(/../g) ?? []).map((h) => parseInt(h, 16)));

async function derive(password: string, salt: Uint8Array, iterations: number): Promise<string> {
  const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(password), "PBKDF2", false, ["deriveBits"]);
  return hex(await crypto.subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", salt: salt as BufferSource, iterations }, key, 256));
}

export async function makeVerifier(username: string, password: string, iterations = 120_000, now: number = Date.now()): Promise<Verifier> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return { username, salt: hex(salt), hash: await derive(password, salt, iterations), iterations, createdAt: now };
}

export async function checkVerifier(v: Verifier, password: string): Promise<boolean> {
  const got = await derive(password, unhex(v.salt), v.iterations);
  let diff = got.length ^ v.hash.length;
  for (let i = 0; i < got.length; i++) diff |= got.charCodeAt(i) ^ (v.hash.charCodeAt(i) || 0);
  return diff === 0;
}
